import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ChevronLeft, Check, Loader } from 'lucide-react';
import { getWeekRecipeIds } from '../utils/mealPlanService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import BottomNav from '../components/BottomNav';

// Aldi store sections and their keywords
const ALDI_CATEGORIES = {
  'Produce': ['onion', 'pepper', 'bell pepper', 'tomato', 'lettuce', 'garlic', 'ginger', 'potato', 'carrot', 'celery', 'cucumber', 'broccoli', 'spinach', 'mushroom', 'corn', 'green bean', 'zucchini', 'squash', 'cabbage', 'avocado', 'lime', 'lemon'],
  'Meat & Seafood': ['chicken', 'beef', 'pork', 'ground beef', 'ground turkey', 'sausage', 'bacon', 'steak', 'fish', 'salmon', 'shrimp', 'turkey'],
  'Dairy & Eggs': ['milk', 'cheese', 'butter', 'cream', 'sour cream', 'yogurt', 'egg', 'parmesan', 'mozzarella', 'cheddar'],
  'Bakery': ['bread', 'bun', 'roll', 'tortilla', 'pita', 'baguette', 'croissant'],
  'Pantry': ['rice', 'pasta', 'flour', 'sugar', 'salt', 'oil', 'olive oil', 'vegetable oil', 'stock', 'broth', 'sauce', 'paste', 'can', 'beans', 'vinegar', 'soy sauce', 'seasoning', 'spice', 'italian seasoning', 'garlic powder', 'paprika', 'cumin', 'chili powder', 'oregano', 'basil', 'thyme', 'pepper', 'black pepper'],
  'Frozen': ['frozen', 'ice cream', 'frozen vegetables', 'frozen fruit']
};

function categorizeIngredient(ingredient) {
  const lower = ingredient.toLowerCase();
  
  for (const [category, keywords] of Object.entries(ALDI_CATEGORIES)) {
    for (const keyword of keywords) {
      if (lower.includes(keyword)) {
        return category;
      }
    }
  }
  
  return 'Other';
}

export default function ShoppingList() {
  const [loading, setLoading] = useState(true);
  const [recipes, setRecipes] = useState([]);
  const [shoppingList, setShoppingList] = useState({});
  const [checkedItems, setCheckedItems] = useState(new Set());
  
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  useEffect(() => {
    generateList();
  }, [currentUser]);

  async function generateList() {
  try {
    setLoading(true);
    
    const recipeIds = await getWeekRecipeIds(currentUser.uid);
    
    if (recipeIds.length === 0) {
      setLoading(false);
      return;
    }
    
    const recipeData = [];
    await Promise.all(
      recipeIds.map(async (id) => {
        const recipeDoc = await getDoc(doc(db, 'recipes', id));
        if (recipeDoc.exists()) {
          recipeData.push({ id: recipeDoc.id, ...recipeDoc.data() });
        }
      })
    );
    
    setRecipes(recipeData);
    
    // Collect ALL ingredients (including duplicates)
    const allIngredients = [];
    recipeData.forEach(recipe => {
      recipe.ingredients.forEach(ing => {
        allIngredients.push(ing.trim());
      });
    });
    
    // Consolidate ingredients and track counts
    const ingredientMap = {};
    allIngredients.forEach(ingredient => {
      const lower = ingredient.toLowerCase();
      
      if (!ingredientMap[lower]) {
        ingredientMap[lower] = {
          original: ingredient,
          count: 1
        };
      } else {
        ingredientMap[lower].count++;
      }
    });
    
    // Categorize with count information
    const categorized = {};
    Object.values(ingredientMap).forEach(({ original, count }) => {
      const category = categorizeIngredient(original);
      if (!categorized[category]) {
        categorized[category] = [];
      }
      
      // Add count if more than 1
      const displayText = count > 1 ? `${original} (${count}x)` : original;
      categorized[category].push(displayText);
    });
    
    setShoppingList(categorized);
  } catch (error) {
    console.error('Error generating list:', error);
  } finally {
    setLoading(false);
  }
}

  const totalItems = Object.values(shoppingList).reduce((sum, items) => sum + items.length, 0);
  const checkedCount = checkedItems.size;
  const progress = totalItems > 0 ? Math.round((checkedCount / totalItems) * 100) : 0;

  return (
    <div className="min-h-screen pb-24" style={{ background: 'linear-gradient(135deg, #f8f0f5 0%, #e8e4f3 100%)' }}>
      {/* Flowing Blob Header */}
      <div className="relative h-64 overflow-hidden mb-6">
        <div 
          className="absolute w-80 h-80 rounded-full opacity-90"
          style={{
            background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
            top: '-60px',
            left: '-60px',
            borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%',
            animation: 'morph 8s ease-in-out infinite',
          }}
        />
        
        <div 
          className="absolute w-64 h-64 rounded-full opacity-90"
          style={{
            background: 'linear-gradient(135deg, #fb7185 0%, #fda4af 100%)',
            top: '60px',
            right: '-80px',
            borderRadius: '60% 40% 30% 70% / 50% 60% 40% 50%',
            animation: 'morph 10s ease-in-out infinite reverse',
          }}
        />
        
        <div 
          className="absolute w-56 h-56 rounded-full"
          style={{
            background: 'linear-gradient(135deg, #ffe082 0%, #fff59d 100%)',
            bottom: '-40px',
            left: '90px',
            opacity: 0.85,
            borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%',
            animation: 'morph 12s ease-in-out infinite',
          }}
        />
        
        <div className="relative z-10 max-w-md mx-auto px-6 pt-8">
          <div className="flex items-center gap-3 mb-2">
            <button 
              onClick={() => navigate('/calendar')} 
              className="bg-white/20 backdrop-blur-sm p-2 rounded-xl hover:bg-white/30 transition-colors"
              style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
            >
              <ChevronLeft size={24} className="text-white" />
            </button>
            <h1 
              className="text-white text-3xl logo"
              style={{ textShadow: '0 2px 20px rgba(0,0,0,0.3), 0 4px 8px rgba(0,0,0,0.2)' }}
            >
              Shopping List
            </h1>
          </div>
          <p 
            className="text-white/95 text-sm pl-14"
            style={{ textShadow: '0 2px 10px rgba(0,0,0,0.2)' }}
          >
            Organized by Aldi sections
          </p>
        </div>
      </div>

      {/* Content - Added padding top for spacing */}
      <div className="max-w-md mx-auto px-6 pt-6">
        {/* Progress Card */}
        {totalItems > 0 && (
          <div className="bg-white rounded-3xl p-6 mb-6 shadow-md">
            <div className="flex items-center justify-between mb-3">
              <span className="font-bold text-gray-800">Progress</span>
              <span className="text-purple-600 font-bold">{checkedCount} / {totalItems}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div 
                className="h-full rounded-full transition-all duration-300"
                style={{
                  width: `${progress}%`,
                  background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
                }}
              />
            </div>
          </div>
        )}

        {/* Shopping List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader size={40} className="animate-spin text-purple-600" />
          </div>
        ) : recipes.length === 0 ? (
          <div className="bg-white rounded-3xl p-10 text-center shadow-md">
            <div className="text-6xl mb-4">📅</div>
            <h3 className="font-bold text-gray-800 mb-2">No meals planned</h3>
            <p className="text-sm text-gray-600 mb-4">
              Plan your week to generate a shopping list
            </p>
            <button
              onClick={() => navigate('/calendar')}
              className="text-purple-600 font-semibold text-sm hover:underline"
            >
              Go to calendar →
            </button>
          </div>
        ) : (
          <div className="space-y-6 pb-6">
            {Object.entries(shoppingList).sort().map(([category, items]) => (
              <div key={category} className="bg-white rounded-3xl p-6 shadow-md">
                <h3 className="font-bold text-lg text-purple-600 mb-4">{category}</h3>
                <div className="space-y-3">
                  {items.map((ingredient, idx) => {
                    const isChecked = checkedItems.has(ingredient);
                    return (
                      <button
                        key={idx}
                        onClick={() => toggleItem(ingredient)}
                        className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors text-left"
                      >
                        <div 
                          className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                            isChecked 
                              ? 'bg-purple-600 border-purple-600' 
                              : 'border-gray-300'
                          }`}
                        >
                          {isChecked && <Check size={16} className="text-white" strokeWidth={3} />}
                        </div>
                        <span className={`${isChecked ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                          {ingredient}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
